import { IBiomaterial } from '@zero-dash/types';
import {
  JSX, useEffect, useMemo, useState,
} from 'react';
import { useSnackbar } from 'notistack';
import { Box, styled } from '@mui/material';
import WispResultsTable from './WispResultsTable';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';
import { IWispResultsRow } from '@/types/PatientDashboard/ResultsView.types';
import { getTumourBiosample } from '@/utils/functions/biosamples/getTumourBiosample';
import mapEvent from '@/utils/functions/mapEvent';
import { IDataFile } from '@/types/FileTracker/FileTracker.types';
import { IAnalysisSet } from '@/types/Analysis/AnalysisSets.types';
import { IDiseaseBurdenFetchState } from '@/types/PatientDashboard/PatientTimeline.types';
import LoadingAnimation from '@/components/Animations/LoadingAnimation';
import CustomTypography from '@/components/Common/Typography';
import CustomButton from '@/components/Common/Button';
import { corePalette } from '@/themes/colours';

const ErrorMessage = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '24px',
  width: '100%',
  color: corePalette.grey100,
});

interface IProps {
  analysisSets: IAnalysisSet[];
  diseaseBurden: IDiseaseBurdenFetchState;
  biomaterials: IBiomaterial[];
  onRetry: () => void;
}

/**
 * The analysis sets and WISP results arrive as props rather than being fetched
 * here: the timeline above already loads both for its Disease Burden lane, and
 * its loader batches the biosample ids so the query-string array limit is never
 * exceeded. Only the result files are fetched here, because the timeline asks
 * the file tracker for panel-builder files instead.
 */
export default function WispSection({
  analysisSets,
  diseaseBurden,
  biomaterials,
  onRetry,
}: IProps): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();
  const { enqueueSnackbar } = useSnackbar();

  const [filesByBiosampleId, setFilesByBiosampleId] = useState<Record<string, IDataFile[]>>({});

  const { results: wispResults, isLoading, hasFailure } = diseaseBurden;

  const biosampleIds = useMemo(
    () => wispResults.map((result) => result.biosampleId),
    [wispResults],
  );

  const rows = useMemo<IWispResultsRow[]>(() => {
    const analysisSetById = new Map(analysisSets.map((set) => [set.analysisSetId, set]));
    const biosampleById = new Map(
      analysisSets
        .flatMap((set) => set.biosamples ?? [])
        .map((biosample) => [biosample.biosampleId, biosample]),
    );
    // Container type is a LabMatrix field, so it is reached through the biomaterial
    // the biosample was taken from rather than off the biosample itself.
    const containerTypeByBiomaterialId = new Map(
      biomaterials.map((biomaterial) => [biomaterial.id, biomaterial.containerType]),
    );

    return wispResults.flatMap((result) => {
      const analysisSet = analysisSetById.get(result.analysisSetId);
      const biosample = biosampleById.get(result.biosampleId);

      // A WISP row can name an analysis set outside this patient's set, the same
      // case buildDiseaseBurdenData skips rather than plotting.
      if (!analysisSet || !biosample) {
        return [];
      }

      return [{
        // zcc_sample_wisp is keyed on (analysis_set_id, biosample_id) and a biosample
        // can sit in more than one analysis set, so the biosample id alone repeats and
        // the grid silently collapses those rows. Matches the Disease Burden lane's key.
        id: `${result.analysisSetId}-${biosample.biosampleId}`,
        biosampleId: biosample.biosampleId,
        biomaterialName: biosample.biomaterialName,
        collectionDate: biosample.collectionDate,
        detected: result.detected,
        snvPurity: result.snvPurity,
        lbLod: result.snvLod,
        // An API that predates these columns leaves them off the response entirely, and
        // the row type promises a number or null — a missing metric reads as null here so
        // the grid prints a dash instead of dereferencing undefined.
        variants: result.variants ?? null,
        fragments: result.fragments ?? null,
        vdDuplex: result.vdDuplex ?? null,
        analysisFormedByTmrAt: {
          tumourCollectionDate: getTumourBiosample(analysisSet.biosamples || [])?.collectionDate || '',
          event: mapEvent(analysisSet.sequencedEvent, true),
        },
        type: biosample.specimen,
        containerType: containerTypeByBiomaterialId.get(biosample.biomaterialId) ?? null,
        cfdnaYield: result.cfdnaYield,
        cfdnaFraction: result.cfdnaFraction,
        panel: result.panel,
        files: filesByBiosampleId[result.biosampleId] ?? [],
        assay: biosample.assay || 'Unknown',
      }];
    });
  }, [analysisSets, biomaterials, filesByBiosampleId, wispResults]);

  useEffect(() => {
    if (!biosampleIds.length) {
      setFilesByBiosampleId({});
      return undefined;
    }

    let cancelled = false;
    zeroDashSdk.filetracker.getFilesByMapping({
      sampleIds: biosampleIds,
      category: 'liquid_biopsy',
      mapping: {
        type: 'wisp',
      },
    })
      .then((files) => {
        if (cancelled) {
          return;
        }
        setFilesByBiosampleId(files.reduce<Record<string, IDataFile[]>>((acc, file) => ({
          ...acc,
          [file.sampleId]: [...(acc[file.sampleId] ?? []), file],
        }), {}));
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setFilesByBiosampleId({});
        enqueueSnackbar('Could not load WISP result files, please try again.', { variant: 'error' });
      });

    return () => {
      cancelled = true;
    };
  }, [biosampleIds, enqueueSnackbar, zeroDashSdk.filetracker]);

  if (isLoading) {
    return <LoadingAnimation />;
  }

  // A partial failure still has rows worth showing, and the timeline's loader has already
  // raised a snackbar for it, so only a fetch that produced nothing offers the retry.
  if (hasFailure && !rows.length) {
    return (
      <ErrorMessage>
        <CustomTypography variant="bodySmall">Could not load WISP results.</CustomTypography>
        <CustomButton label="Retry" variant="outline" size="small" onClick={onRetry} />
      </ErrorMessage>
    );
  }

  return (
    <WispResultsTable
      rows={rows}
    />
  );
}
